'use client';

import { AlertTriangle } from 'lucide-react';

import { cn } from '@/lib/utils';

type AppErrorFallbackProps = {
  title?: string;
  description?: string;
  actionLabel?: string;
  onAction?: () => void;
  showIcon?: boolean;
  className?: string;
};

export function AppErrorFallback({
  title = 'Something went wrong',
  description = 'A technical issue interrupted this experience. Please try again.',
  actionLabel = 'Try Again',
  onAction,
  showIcon = true,
  className,
}: AppErrorFallbackProps) {
  return (
    <div
      data-slot="app-error-fallback"
      data-hook="app-error-fallback"
      className={cn(
        'flex min-h-80 w-full items-center justify-center px-6 py-10',
        className,
      )}
    >
      <div className="mx-auto flex w-full max-w-140 flex-col items-center rounded-3xl border border-red-100 bg-white px-8 py-10 text-center shadow-sm">
        {showIcon ? (
          <div className="flex size-12 items-center justify-center rounded-full bg-red-50 text-red-500">
            <AlertTriangle className="size-5" aria-hidden />
          </div>
        ) : null}
        <h2
          className="mt-5 text-[22px] font-semibold tracking-tight text-[#222B45]"
          data-hook="app-error-fallback-title"
        >
          {title}
        </h2>
        <p
          className="mt-3 max-w-105 text-[15px] leading-6 text-[#5F6C80]"
          data-hook="app-error-fallback-description"
        >
          {description}
        </p>
        {onAction ? (
          <button
            type="button"
            onClick={onAction}
            className="mt-6 h-10 rounded-full bg-primary px-5 text-sm font-medium text-primary-foreground hover:opacity-90"
            data-hook="app-error-fallback-action"
          >
            {actionLabel}
          </button>
        ) : null}
      </div>
    </div>
  );
}
